import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { PopUpNotificationDto } from './models/popup-notification-dto.model';
import { PopUpNotificationLanguageModel } from './models/popup-notification-language.model';
import { PopUpNotificationViewModel } from './models/popup-notification-view.model';
import { PopUpNotificationState, PopUpNotificationStore } from './popup-notification.store';
import { config } from '../../../shared/config';

@Injectable({ providedIn: 'root' })
export class PopUpNotificationQuery extends QueryEntity<PopUpNotificationState> {
  constructor(protected store: PopUpNotificationStore) {
    super(store);
  }

  /**
   * Retrieves the popup notification for a given page
   * @param pageUrl the page to retreive popup notification for ('' for Home page)
   * @param lcid LCID of the required popup notification
   * @returns the popup notification associated with the given pageUrl, otherwise undefined
   */
  getPopUpNotificationForPage(pageUrl: string, lcid: number): PopUpNotificationViewModel | undefined {
    if (pageUrl && lcid) {
      // strip trailing forward slash for non homepage pages
      if (pageUrl.length > 1) {
        pageUrl = pageUrl.replace(/\/$/, '');
      }

      // Undefined page urls are to be shown on all pages
      let popUpNotification = this.getAll().filter((popup) => popup.pageUrl == pageUrl || popup.pageUrl == undefined)[0];
      if (popUpNotification) {
        let popUpNotificationViewModel = popUpNotification as unknown as PopUpNotificationViewModel;
        if (popUpNotification.popUpNotificationLanguages) {
          let popUpNotificationLanguage = this.popUpNotificationLanguageForLcid(popUpNotification, lcid);
          popUpNotificationViewModel = Object.assign({}, popUpNotificationViewModel, {
            popUpNotificationLanguage: popUpNotificationLanguage,
          });
        }
        return popUpNotificationViewModel;
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  /**
   * Retrieves the popup notification language variant for a given LCID
   * @param popUpNotification Popup notification data object
   * @param lcid LCID of the required popup notification
   * @returns The language variant associated with the given lcid otherwise the original data as default.
   */
  popUpNotificationLanguageForLcid(
    popUpNotification: PopUpNotificationDto,
    lcid: number
  ): PopUpNotificationLanguageModel | undefined {
    const availableLanguages = popUpNotification.popUpNotificationLanguages;
    if (availableLanguages.length && lcid) {
      if (availableLanguages.some((popUpNotificationLanguage) => popUpNotificationLanguage.lCID === lcid)) {
        return availableLanguages.find((popUpNotificationLanguage) => popUpNotificationLanguage.lCID === lcid);
      } else {
        return this.defaultLanguage(popUpNotification);
      }
    }
    return this.defaultLanguage(popUpNotification);
  }

  /**
   * Retrieves the original data as default language for a popup notification
   * @param popUpNotification Popup notification data object
   * @returns The original data as default language variant.
   */
  defaultLanguage(popUpNotification: PopUpNotificationDto): PopUpNotificationLanguageModel {
    return {
      description: popUpNotification.description,
      lCID: +config.defaultLcid,
      name: popUpNotification.name,
      popUpNotificationId: popUpNotification.popUpNotificationId,
    };
  }
}
